import React, { useEffect, useState, useCallback, useMemo } from "react";
import { API_BASE_URL, getHeaders } from "../utils/api";
import { readAdminApiErrorMessage } from "../utils/adminUi";
import { showToast } from "../utils/toast";
import {
  Users,
  Mail,
  AlertCircle,
  CheckCircle,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  Activity,
  ChevronLeft,
  ChevronRight,
  Info
} from "lucide-react";

export default function PendingUsersManagement({ token, roles = [], orgId }) {
  // Data State
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // UI & UX State
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [expandedId, setExpandedId] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const normalizedRoles = useMemo(
    () => (Array.isArray(roles) ? roles.map((role) => String(role).toLowerCase()) : []),
    [roles]
  );
  const isSystemAdmin = normalizedRoles.includes("systemadministrator");
  const isOrgAdmin = normalizedRoles.includes("organizationadministrator");
  const canReviewPendingUsers = isSystemAdmin || isOrgAdmin;

  const fetchPendingUsers = useCallback(async () => {
    if (!token || !canReviewPendingUsers) {
      setUsers([]);
      setFilteredUsers([]);
      return;
    }

    if (isOrgAdmin && !orgId) {
      setUsers([]);
      setFilteredUsers([]);
      showToast.error("Unable to resolve organization context for pending approvals.");
      return;
    }

    setLoading(true);
    try {
      const headers = getHeaders(token);
      let url = `${API_BASE_URL}/users/status/pending`;
      if (isOrgAdmin && orgId) {
        url += `?organizationId=${orgId}`;
      }

      const res = await fetch(url, { headers });

      if (!res.ok) {
        const message = await readAdminApiErrorMessage(res, {
          400: "Pending approvals request is invalid.",
          401: "Your session expired. Please sign in again.",
          403: "You do not have permission to review pending users.",
          default: "Unable to load pending users. Please try again."
        });
        throw new Error(message);
      }

      const data = await res.json();
      setUsers(data || []);
    } catch (err) {
      showToast.error(err?.message || "Unable to load pending users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, canReviewPendingUsers, isOrgAdmin, orgId]);

  useEffect(() => {
    if (token) fetchPendingUsers();
  }, [fetchPendingUsers, token]);

  // Filtering & Sorting Logic
  useEffect(() => {
    let filtered = [...users];
    
    if (searchTerm) {
      filtered = filtered.filter(user => 
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (sortBy === "newest") {
      filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === "name") {
      filtered.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
    }
    
    setFilteredUsers(filtered);
    setCurrentPage(1);
    setExpandedId(null);
  }, [users, searchTerm, sortBy]);

  // Pagination Logic
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const confirmAction = async (userId, action) => {
    if (!canReviewPendingUsers) {
      showToast.error("You do not have permission to process pending users.");
      return;
    }

    if (isOrgAdmin && !orgId) {
      showToast.error("Unable to resolve organization context for this action.");
      return;
    }

    const message =
      action === "approve"
        ? "Approve this user and grant them access?"
        : "Reject this user? This action cannot be undone.";

    if (!window.confirm(message)) return;

    setProcessing(userId);
    try {
      const headers = getHeaders(token);
      const targetUser = users.find((u) => u.id === userId);
      const resolvedOrgId = orgId || targetUser?.organizationId;

      if (!resolvedOrgId) {
        throw new Error("Unable to resolve organization context for this action.");
      }

      let url = `${API_BASE_URL}/users/${userId}/${action}`;
      url += `?organizationId=${resolvedOrgId}`;

      const res = await fetch(url, { method: "PUT", headers });

      if (!res.ok) {
        const responseMessage = await readAdminApiErrorMessage(res, {
          400: `Unable to ${action} user. Please verify request details and try again.`,
          401: "Your session expired. Please sign in again.",
          403: `You do not have permission to ${action} this user.`,
          404: "User not found.",
          default: `Failed to ${action} user. Please try again.`
        });

        throw new Error(responseMessage);
      }

      showToast.success(`User ${action === "approve" ? "approved" : "rejected"} successfully!`);
      fetchPendingUsers();
    } catch (err) {
      showToast.error(err?.message || `Failed to ${action} user. Please try again.`);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Block */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-600 rounded-xl text-white shadow-lg">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pending Approvals</h1>
                <p className="text-slate-500 text-sm font-medium">Review and authorize new member registrations</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <div className="px-6 py-3 rounded-xl bg-amber-50 border border-amber-100 text-center min-w-[100px]">
                <div className="text-2xl font-black text-amber-600">{users.length}</div>
                <div className="text-[10px] uppercase font-bold text-amber-500 tracking-widest">Awaiting Review</div>
              </div>
            </div>
          </div>
        </div>

        {!canReviewPendingUsers && (
          <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center text-center border border-red-200 shadow-sm">
            <div className="p-4 bg-red-50 rounded-full mb-4">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Access denied</h3>
            <p className="text-slate-500 text-sm max-w-xs">
              Pending user approvals are available to Organization Administrator and System Administrator roles only.
            </p>
          </div>
        )}

        {/* Filter Bar */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-8 bg-white rounded-xl shadow-sm border border-slate-200 p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search by name or email address..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-transparent outline-none text-sm font-medium"
              />
            </div>
          </div>
          <div className="md:col-span-4 bg-white rounded-xl shadow-sm border border-slate-200 p-2">
            <div className="relative flex items-center">
              <Filter className="absolute left-3 w-4 h-4 text-slate-400" />
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)} 
                className="w-full pl-10 pr-8 py-2.5 bg-transparent outline-none text-sm font-bold appearance-none cursor-pointer"
              >
                <option value="newest">Newest Requests</option>
                <option value="oldest">Oldest Requests</option>
                <option value="name">Sort by Name</option>
              </select>
              <ChevronDown className="absolute right-3 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Content List */}
        {canReviewPendingUsers && <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-2xl p-20 flex flex-col items-center justify-center border border-slate-200 border-dashed">
              <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
              <p className="text-slate-500 font-bold text-sm tracking-widest uppercase">Fetching Records...</p>
            </div>
          ) : paginatedItems.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 flex flex-col items-center justify-center text-center border border-slate-200 shadow-sm">
              <div className="p-4 bg-emerald-50 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {searchTerm ? "No results found" : "Inbox is empty!"}
              </h3>
              <p className="text-slate-500 text-sm max-w-xs">
                {searchTerm 
                  ? "Try adjusting your search criteria to find the user." 
                  : "All registration requests have been processed. Great job!"}
              </p>
            </div>
          ) : (
            paginatedItems.map((user) => {
              const isExpanded = expandedId === user.id;
              return (
                <div key={user.id} className={`bg-white rounded-2xl border transition-all duration-300 shadow-sm ${
                  isExpanded ? 'border-indigo-300 ring-4 ring-indigo-50' : 'border-slate-200 hover:shadow-md'
                } ${processing === user.id ? 'opacity-60 grayscale' : ''}`}>
                  
                  {/* Card Header */}
                  <div 
                    onClick={() => setExpandedId(isExpanded ? null : user.id)}
                    className="p-5 flex items-center justify-between cursor-pointer group"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-sm">
                          {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-500 rounded-full border-2 border-white flex items-center justify-center">
                          <Clock className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                          {user.firstName} {user.lastName}
                        </h4>
                        <div className="flex items-center gap-3 text-xs font-medium text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {user.email}
                          </span>
                          <span className="hidden sm:inline opacity-30">•</span>
                          <span className="flex items-center gap-1 text-indigo-600 font-bold">
                            <Calendar className="w-3 h-3" /> {formatDate(user.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="hidden sm:block px-2.5 py-1 rounded-full text-[10px] uppercase font-black border bg-amber-50 text-amber-700 border-amber-100">
                        Pending
                      </span>
                      <div className={`p-2 rounded-lg bg-slate-50 transition-colors ${isExpanded ? 'rotate-180 text-indigo-600' : 'text-slate-400'}`}>
                        <ChevronDown className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 p-6 bg-slate-50/40 rounded-b-2xl animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* User Details Section */}
                        <div className="space-y-5">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Info className="w-4 h-4 text-indigo-600" />
                              <h5 className="text-sm font-bold text-slate-800">Registration Profile</h5>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-inner space-y-3">
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400 font-bold uppercase tracking-wider">Full Name</span>
                                <span className="text-slate-900 font-bold">{user.firstName} {user.lastName}</span>
                              </div>
                              <div className="flex justify-between text-xs border-t border-slate-50 pt-3">
                                <span className="text-slate-400 font-bold uppercase tracking-wider">Email</span>
                                <span className="text-slate-900 font-bold">{user.email}</span>
                              </div>
                              <div className="flex justify-between text-xs border-t border-slate-50 pt-3">
                                <span className="text-slate-400 font-bold uppercase tracking-wider">Requested On</span>
                                <span className="text-slate-900 font-bold">{new Date(user.createdAt).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-3 justify-center lg:items-end">
                           <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Administrative Controls</label>
                           <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                              <button 
                                disabled={processing === user.id}
                                onClick={(e) => { e.stopPropagation(); confirmAction(user.id, "reject"); }}
                                className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-white border border-slate-200 text-red-600 rounded-xl text-xs font-black hover:bg-red-50 hover:border-red-200 transition-all shadow-sm active:scale-95"
                              >
                                {processing === user.id ? 'Wait...' : (
                                  <>
                                    <UserX className="w-4 h-4" /> Reject Access
                                  </>
                                )}
                              </button>
                              
                              <button 
                                disabled={processing === user.id}
                                onClick={(e) => { e.stopPropagation(); confirmAction(user.id, "approve"); }}
                                className="flex-1 lg:w-64 flex items-center justify-center gap-2 px-8 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black shadow-md hover:bg-indigo-700 transition-all active:scale-95"
                              >
                                {processing === user.id ? (
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                  <>
                                    <UserCheck className="w-4 h-4" /> Approve & Activate
                                  </>
                                )}
                              </button>
                           </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>}

        {/* Pagination Controls */}
        {canReviewPendingUsers && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="text-sm font-medium text-slate-500">
              Showing <span className="font-bold text-slate-900">{paginatedItems.length}</span> of <span className="font-bold text-slate-900">{filteredUsers.length}</span> users
            </div>
            <div className="flex items-center gap-1">
              <button 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i} onClick={() => setCurrentPage(i + 1)} 
                  className={`min-w-[40px] h-10 rounded-lg text-sm font-bold transition-all ${
                    currentPage === i + 1 ? 'bg-indigo-600 text-white shadow-lg' : 'hover:bg-slate-50 text-slate-600 border border-transparent hover:border-slate-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button 
                disabled={currentPage === totalPages} 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Footer Info */}
        {canReviewPendingUsers && users.length > 0 && (
          <div className="flex items-center justify-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest pt-4">
            <Activity className="w-3 h-3 text-indigo-500" />
            <span>Secure Access Management Portal</span>
          </div>
        )}
      </div>
    </div>
  );
}